import {
  Event,
  FormResponseV2,
  AccountData,
  FormCheckBoxField,
  FormInputType,
  FormRadioField,
  SavedFormQuestion,
} from "@markit/common.types";
import FlatList from "flatlist-react/lib";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { LightTheme, useTheme } from "../../hooks/useTheme";
import { CircularProgress, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  getEventFormQuestions,
  getEventFormResponses,
  questionInputName,
} from "../../utils/eventUtils/formUtils";
import { FormResponseContainer } from "./FormResponseContainer";
import { useLoadUserList } from "../../hooks/useLoadUserList";
import filter from "lodash.filter";
import PopupModalContainer from "../Containers/PopupModalContainer";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import CreateFormAndTrackingLinkModal from "../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { ContainerButton } from "../Buttons/ContainerButton";
import { formatCheckboxRadioAnswersLine } from "@markit/common.utils";

type FormsPanelProps = {
  event: Event;
  dashboardVisibility: number;
};

export const FormsPanel = (props: FormsPanelProps) => {
  const { event, dashboardVisibility } = props;
  const { theme } = useTheme();
  const { account } = useSelector(getAccountState);
  const { accountData } = account;

  const [formQuestions, setFormQuestions] = useState<SavedFormQuestion[]>([]);
  const [responses, setResponses] = useState<FormResponseV2[]>([]);
  const [loadingResponses, setLoadingResponses] = useState<boolean>(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [currFormQuestion, setCurrFormQuestion] = useState<SavedFormQuestion>();
  const [currUser, setCurrUser] = useState<FormResponseV2>();
  const [currUserName, setCurrUserName] = useState<string>("");

  const [isUserResponseModalOpen, setIsUserResponseModalOpen] =
    useState<boolean>(false);
  const [createFormVisible, setCreateFormVisible] = useState(false);
  const [aggregateRefreshKey, setAggregateRefreshKey] = useState(0);

  const styles = {
    bigNumberText: {
      fontSize: 24,
    },

    containerView: {
      paddingInline: 14,
    },

    formResponsesInfo: {
      gap: 24,
      color: Colors.BLACK,
    },

    headerTitle: {
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 4,
    },

    listView: {
      height: 380,
      marginTop: 18,
      paddingBottom: 40,
    },

    noFormQuestionsView: {
      paddingBlock: 12,
      paddingInline: 14,
      borderRadius: 12,
      backgroundColor: Colors.GRAY6,
      fontSize: 12,
    },

    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
    },

    subText: {
      color: theme.SubText.color,
      fontSize: 12,
    },
  };

  useAsyncOnMount(async () => {
    const [formQuestions, responses] = await Promise.all([
      getEventFormQuestions(event),
      getEventFormResponses(event.id),
    ]);
    setFormQuestions(formQuestions);
    setResponses(responses);
    setCurrFormQuestion(formQuestions[0]);
    setLoadingResponses(false);
  });

  const hideCreationAndCopying = useMemo(
    () => !accountData.isAdmin && dashboardVisibility < 2,
    [accountData.isAdmin, dashboardVisibility]
  );

  const userIdList = useMemo(
    () => responses.map((response) => response.uid),
    [responses]
  );

  const {
    isFinished,
    isLoading,
    fetchedUserData,
    loadMoreUsers,
    loadSearchResultsThrottled,
  } = useLoadUserList({
    userIdList: !loadingResponses ? userIdList : [],
    windowSize: 40,
  });

  const contains = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = useCallback(
    (text: string) => {
      setSearchTerm(text.toLowerCase());
      loadSearchResultsThrottled(text.toLowerCase());
    },
    [loadSearchResultsThrottled]
  );

  /** Keeps list rows in sync when a row is edited via SingleEditCustomData (modal only updates currUser otherwise). */
  const applyUserFormResponseUpdate = useCallback((updated: FormResponseV2) => {
    setCurrUser(updated);
    setResponses((prev) =>
      prev.map((r) => (r.uid === updated.uid ? updated : r))
    );
    setAggregateRefreshKey((k) => k + 1);
  }, []);

  const responsesToShow: AccountData[] = useMemo(() => {
    let userList: AccountData[] = [];
    userList = fetchedUserData;
    if (searchTerm !== "") {
      userList = filter(userList, (user: AccountData) => {
        return contains(user, searchTerm);
      });
    }
    return userList;
  }, [fetchedUserData, searchTerm, contains]);

  useEffect(() => {
    if (!loadingResponses) {
      loadMoreUsers();
    }
  }, [loadMoreUsers, loadingResponses]);

  if (loadingResponses) {
    return (
      <div className="Centering" style={{ height: "60vh" }}>
        <CircularProgress style={{ color: Colors.BLACK }} size={30} />
      </div>
    );
  }

  return (
    <div className="RowNormal" style={{ gap: 30 }}>
      <div className="ColumnNormal" style={{ width: 355, minWidth: 355 }}>
        <StandardBorderedContainer
          containerStyles={{
            ...styles.overallContainerView,
            paddingBottom: 14,
          }}
        >
          <div className="ColumnNormal">
            <div style={{ ...styles.containerView, paddingTop: 14 }}>
              <div style={styles.headerTitle}>Manage Collected Data</div>
              <div style={styles.subText}>
                Collect and manage data/responses.
              </div>
            </div>
            <HorizontalDivider altMargin={14} />
            {formQuestions.length > 0 ? (
              <>
                <div style={styles.containerView}>
                  <div className="RowNormal" style={styles.formResponsesInfo}>
                    <div>
                      <div style={styles.bigNumberText}>
                        {formQuestions.length}
                      </div>
                      <div style={styles.subText}>
                        Active Question{formQuestions.length > 1 && "s"}
                      </div>
                    </div>
                    <div>
                      <div style={styles.bigNumberText}>{responses.length}</div>
                      <div style={styles.subText}>
                        Unique Response{responses.length > 1 && "s"}
                      </div>
                    </div>
                  </div>
                </div>
                <HorizontalDivider altMargin={14} />
              </>
            ) : null}
            <div style={styles.containerView}>
              {formQuestions.length > 0 ? (
                <div className="ColumnNormal" style={{ gap: 12 }}>
                  <FlatList
                    list={formQuestions}
                    scrollEnabled={false}
                    keyExtractor={(item: SavedFormQuestion) => item.id}
                    renderItem={(item) => {
                      return (
                        <div
                          className="ColumnNormal"
                          style={{
                            gap: 7,
                            padding: "12px",
                            borderRadius: "8px",
                            backgroundColor:
                              item.id === currFormQuestion?.id
                                ? Colors.GRAY6
                                : Colors.WHITE,
                            cursor: "pointer",
                          }}
                          onClick={() => setCurrFormQuestion(item)}
                        >
                          <span
                            style={{
                              color: Colors.BLACK,
                              fontSize: 14,
                              fontWeight: "600",
                            }}
                          >
                            {item.formInput.label}
                          </span>
                          <span style={{ fontSize: "12px" }}>
                            {questionInputName(item.formInput.inputType)}
                          </span>
                        </div>
                      );
                    }}
                  />
                </div>
              ) : (
                <div style={styles.noFormQuestionsView}>
                  No form questions to show
                </div>
              )}
            </div>
          </div>
        </StandardBorderedContainer>
      </div>
      <div className="ColumnNormal" style={{ flex: 1 }}>
        <StandardBorderedContainer
          containerStyles={{
            ...styles.overallContainerView,
          }}
        >
          <div
            className="ColumnNormal"
            style={{ paddingBlock: 14, minHeight: 480 }}
          >
            {formQuestions.length > 0 ? (
              <>
                <FormResponseContainer
                  eventId={event.id}
                  item={currFormQuestion}
                  clicked={true}
                  onPress={() => {}}
                  hideInactiveOptions
                  containerStyles={{ marginInline: 14 }}
                  aggregateRefreshKey={aggregateRefreshKey}
                />
                <HorizontalDivider altMargin={14} />
                <div style={styles.containerView}>
                  <TextField
                    placeholder="Search by name"
                    variant="standard"
                    onChange={(event) => handleSearch(event.target.value)}
                    style={{
                      backgroundColor: Colors.GRAY11,
                      borderRadius: "12px",
                      border: "none",
                      width: "100%",
                    }}
                    inputProps={{
                      style: {
                        padding: "10px",
                        paddingLeft: "7px",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <Icon
                          icon="ion:search"
                          height={24}
                          style={{ marginLeft: "10px", color: Colors.GRAY2 }}
                        />
                      ),
                      disableUnderline: true,
                    }}
                  />
                  <div
                    className="HideScrollbar"
                    style={{ ...styles.listView, overflowY: "scroll" }}
                  >
                    <FlatList
                      list={responsesToShow}
                      keyExtractor={(item: AccountData) => item.uid}
                      renderItem={(item) => {
                        const userFound = responses.find(
                          (response) => response.uid === item.uid
                        );

                        if (
                          userFound &&
                          currFormQuestion &&
                          userFound.answers[currFormQuestion.id]
                        ) {
                          const foundAnswer =
                            userFound.answers[currFormQuestion.id];
                          const formattedAnswer =
                            currFormQuestion.formInput.inputType ===
                              FormInputType.CHECK_BOX ||
                            currFormQuestion.formInput.inputType ===
                              FormInputType.RADIO
                              ? formatCheckboxRadioAnswersLine(
                                  foundAnswer,
                                  (
                                    currFormQuestion.formInput as
                                      | FormCheckBoxField
                                      | FormRadioField
                                  ).options ?? [],
                                  (
                                    currFormQuestion.formInput as
                                      | FormCheckBoxField
                                      | FormRadioField
                                  ).inactiveOptions ?? []
                                )
                              : foundAnswer.every((response) => response === "")
                                ? "No Response Collected"
                                : currFormQuestion.formInput.inputType ===
                                    FormInputType.FILE_UPLOAD
                                  ? "View File"
                                  : foundAnswer.join(", ");
                          return (
                            <div
                              style={{
                                paddingInline: "4px",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setCurrUser(userFound);
                                setCurrUserName(item.fullName);
                                setIsUserResponseModalOpen(true);
                              }}
                            >
                              <div
                                style={{
                                  ...(formattedAnswer ===
                                  "No Response Collected"
                                    ? theme.SubText
                                    : theme.PrimaryText),
                                  fontSize: 14,
                                  cursor: "pointer",
                                }}
                              >
                                {formattedAnswer}
                              </div>
                              <div
                                className="AlignedRow"
                                style={{ cursor: "pointer", marginTop: 7 }}
                              >
                                <div
                                  className="AlignedRow"
                                  style={{
                                    ...styles.subText,
                                    cursor: "pointer",
                                  }}
                                >
                                  {item.fullName}
                                </div>
                                <Icon
                                  icon={"mdi:chevron-right"}
                                  height={20}
                                  style={{
                                    color: Colors.GRAY2,
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                              {responsesToShow[responsesToShow.length - 1] !==
                                item && <hr />}
                            </div>
                          );
                        } else {
                          return null;
                        }
                      }}
                      renderWhenEmpty={() => (
                        <div>
                          <span style={theme.SubText}>
                            {isFinished || userIdList.length === 0
                              ? "No users to show"
                              : "Loading..."}
                          </span>
                        </div>
                      )}
                      hasMoreItems={!isFinished}
                      loadMoreItems={() => {
                        loadMoreUsers();
                      }}
                      paginationLoadingIndicator={() =>
                        isLoading && !isFinished ? (
                          <CircularProgress
                            style={{
                              color: "#929292",
                              alignSelf: "center",
                              marginTop: "24px",
                            }}
                            size={30}
                          />
                        ) : null
                      }
                      paginationLoadingIndicatorPosition="center"
                    />
                  </div>
                </div>
              </>
            ) : (
              <EmptyStateButton
                title={"No Form Questions"}
                description={
                  hideCreationAndCopying
                    ? "You can't add form questions unless you are the host"
                    : "Collect additional info from your guests like email, phone number, or social media links"
                }
                icon={
                  <Icon
                    icon={"ion:documents"}
                    height={49}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                iconBox={73}
                containerStyles={{ paddingTop: 120 }}
              />
            )}
          </div>
        </StandardBorderedContainer>
        {isUserResponseModalOpen ? (
          <PopupModalContainer
            headerComp={
              <div className="AlignedRow" style={{ gap: 10 }}>
                <ContainerButton
                  onPress={() => setIsUserResponseModalOpen(false)}
                  theme={LightTheme}
                  iconName={"ion:close"}
                  iconSize={18}
                  containerStyles={{ width: 31, height: 31, padding: 0 }}
                />
                <div>{currUserName}&apos;s Form Responses</div>
              </div>
            }
            valueComp={
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <FlatList
                  list={formQuestions}
                  keyExtractor={(item: SavedFormQuestion) => item.id}
                  renderItem={(item) => {
                    return (
                      <FormResponseContainer
                        eventId={event.id}
                        item={item}
                        individualUserResponse={currUser}
                        setIndividualUserResponse={applyUserFormResponseUpdate}
                        hideInactiveOptions
                        clicked={true}
                        onPress={() => {}}
                      />
                    );
                  }}
                />
              </div>
            }
            noExit
            closeModal={() => setIsUserResponseModalOpen(false)}
          />
        ) : null}
        {createFormVisible ? (
          <CreateFormAndTrackingLinkModal
            isVisible={createFormVisible}
            setIsVisible={setCreateFormVisible}
            existingForm={event}
            eventType={event.eventType}
          />
        ) : null}
      </div>
    </div>
  );
};
